/**
 * The lines of a proposal.
 *
 * Editable as a whole and saved as a whole, which is what the API expects: the
 * update replaces every line, because a per-line endpoint under one save button
 * turns a single visible change into several calls that can half-fail.
 *
 * The amount column is a **preview**. The server multiplies and quantises, and
 * this shows what that will produce so nobody is surprised by a rounding they
 * cannot see. It is never sent — a total the caller supplies is a total the
 * caller can get wrong.
 */

import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button, Checkbox, Field, Input, addMoney, cn, formatMoney, multiplyMoney, percentOfMoney, sumMoney } from '@connect/web-shared';

export interface DraftLine {
  description: string;
  quantity: string;
  unit_rate: string;
  taxable: boolean;
}

export const EMPTY_LINE: DraftLine = {
  description: '',
  quantity: '1',
  unit_rate: '',
  taxable: true,
};

/**
 * Quantity times rate, to two places, as a decimal **string**. Display only.
 *
 * A string, and computed by `multiplyMoney`, because the preview's whole job is
 * to show the rounding before it happens — so it must round the way the server
 * does. In floating point it did not: `Math.round(1.005 * 1 * 100) / 100` is
 * 1.00 where the proposal will say 1.01, because 1.005 has no exact binary form
 * and lands under the half it should round up from.
 *
 * `'0.00'` for a line still being typed into, so a half-finished box shows a
 * zero rather than `NaN` on a document about to be sent.
 */
export function lineAmount(line: DraftLine): string {
  return multiplyMoney(line.quantity, line.unit_rate) ?? '0.00';
}

export function ProposalLinesEditor({
  value,
  onChange,
  currency,
  taxRate,
  disabled,
}: {
  value: DraftLine[];
  onChange: (next: DraftLine[]) => void;
  currency: string;
  /** As a percentage string, for the preview. */
  taxRate: string;
  disabled?: boolean;
}) {
  const update = (index: number, patch: Partial<DraftLine>) => {
    onChange(value.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  // Summed and taxed in decimal for the same reason the lines are: these are
  // the three figures somebody checks against the total before sending.
  const subtotal = sumMoney(value.map(lineAmount));
  const taxable = sumMoney(value.filter((line) => line.taxable).map(lineAmount));
  const tax = percentOfMoney(taxable, taxRate);
  const total = addMoney(subtotal, tax);

  return (
    <Field label="Lines" hint="Quantity times rate. A negative rate is a discount.">
      <div className="space-y-2">
        {value.map((line, index) => (
          <div
            key={index}
            className="grid items-end gap-2 rounded-control border border-line p-2 sm:grid-cols-[auto_1fr_5rem_7rem_auto_auto]"
          >
            <GripVertical className="mb-2.5 size-3.5 shrink-0 text-faint" aria-hidden />
            <Input
              aria-label="Description"
              placeholder="Discovery workshop"
              value={line.description}
              disabled={disabled}
              onChange={(event) => update(index, { description: event.target.value })}
            />
            <Input
              aria-label="Quantity"
              inputMode="decimal"
              value={line.quantity}
              disabled={disabled}
              onChange={(event) => update(index, { quantity: event.target.value })}
            />
            <Input
              aria-label="Rate"
              inputMode="decimal"
              placeholder="0.00"
              value={line.unit_rate}
              disabled={disabled}
              onChange={(event) => update(index, { unit_rate: event.target.value })}
            />
            <span className="mb-2 w-28 shrink-0 text-right text-sm text-body tabular">
              {formatMoney(lineAmount(line), currency)}
            </span>
            <span className="mb-1.5 flex items-center gap-2">
              <Checkbox
                aria-label="Taxable"
                label="Tax"
                checked={line.taxable}
                disabled={disabled}
                onChange={(event) => update(index, { taxable: event.target.checked })}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label="Remove this line"
                disabled={disabled}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </span>
          </div>
        ))}

        {value.length === 0 ? (
          <p className="rounded-control border border-dashed border-line px-3 py-3 text-xs text-faint">
            No lines yet. A proposal cannot be sent empty.
          </p>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => onChange([...value, { ...EMPTY_LINE }])}
          >
            <Plus className="size-3.5" />
            Add a line
          </Button>

          {value.length > 0 ? (
            <span className={cn('text-right text-xs text-muted')}>
              <span className="block">
                Subtotal{' '}
                <span className="text-body tabular">
                  {formatMoney(subtotal, currency)}
                </span>
              </span>
              <span className="block">
                Tax{' '}
                <span className="text-body tabular">{formatMoney(tax, currency)}</span>
              </span>
              <span className="block font-medium text-heading">
                Total{' '}
                <span className="tabular">
                  {formatMoney(total, currency)}
                </span>
              </span>
            </span>
          ) : null}
        </div>
      </div>
    </Field>
  );
}
